import { DatePicker, TimePicker, Tooltip } from 'antd';
import { TooltipTrigger } from 'antd/lib/tooltip';
import { DatePickerProps } from 'antd/lib/date-picker/interface';
import { TimePickerProps } from 'antd/lib/time-picker';
import { momentOrNull } from 'common/util';
import moment, { Moment } from 'moment';
import React, { HTMLAttributes } from 'react';
import './index.less';
import cn from 'classnames';
import {ValidationRule} from "antd/lib/form";

const BASE_CLS = "mc-date-time-picker"

// export interface DateTimePickerValue {
//     date: Moment,
//     time: Moment
// }

export class DateTimePickerValue {
    date: Moment
    time: Moment
    private originalDateTime?: Moment

    constructor(date: string | Moment, time: string | Moment, options={ utc: false }){
        this.date = momentOrNull(date, { utc: options?.utc });
        this.time = momentOrNull(time, { utc: options?.utc });
    }

    valueOf(){
        return this.toString();
    }

    toMoment(){
        let date = this.date?.clone();
        if (!date) return null;

        date.set({
            hours: 0,
            minutes: 0,
            seconds: 0,
            milliseconds: 0
        })

        let time = this.time;

        if (!time) return null;

        let t = moment(date);
        t.hour(time.hour());
        t.minute(time.minute());
        t.second(time.second());
        t.millisecond(time.millisecond());
        
        let timeDuration = t.diff(date);

        let dateTime = moment(date);
        dateTime.add(timeDuration);
        // A repeated local hour has two possible instants. Retain the original
        // offset while the displayed date and time are unchanged.
        if (this.originalDateTime && dateTime.format('YYYY-MM-DDTHH:mm:ss.SSS') === this.originalDateTime.format('YYYY-MM-DDTHH:mm:ss.SSS')) {
            return this.originalDateTime.clone();
        }
        return dateTime;
    }

    static toMoment(value: DateTimePickerValue){
        if (!value || typeof value !== 'object') return;
        if ('toMoment' in value){
            return value.toMoment();
        }
    }

    toString(){
        return this.toMoment().toISOString(false);
    }

    static from(dt: string | any, options = { utc: false }): DateTimePickerValue {
        let date: Moment, time: Moment;
        if (dt && typeof dt === 'object' && dt.date && dt.time){
            date = dt.date;
            time = dt.time;
        }
        else if (!momentOrNull(dt)){
            return new DateTimePickerValue(null, null);
        }
        else if (!options?.utc)
        {
            let m = momentOrNull(dt);
            date = moment(m);
            time = moment(m);
        }
        else if (options?.utc)
        {
            let m = momentOrNull(dt, { utc: true });
            date = moment.utc(m);
            time = moment.utc(m);
        }


        date.set({
            hours: 0,
            minutes: 0,
            seconds: 0,
            milliseconds: 0
        })

        time.set({
            date: moment().date()
        })

        const value = new DateTimePickerValue(date, time, { utc: options?.utc });
        if (dt && !(typeof dt === 'object' && dt.date && dt.time)) {
            value.originalDateTime = momentOrNull(dt, {utc: options?.utc})?.clone();
        }
        return value;
    }

    static validate(rule: ValidationRule, value: DateTimePickerValue, callback: (msg?: string) => void){
        if (rule.required){
            if (!value.date){
                callback("Date must be filled in");
            }
            if (!value.time){
                callback("Time must be filled in");
            }
        }

        if (!!value.date !== !!value.time){
            callback('You must fully fill in date and time.');
        }

        callback();
    }
}

export interface DateTimePickerProps {
    value?: DateTimePickerValue,
    onChange?: (value: DateTimePickerValue) => void,
    datePickerProps?: DatePickerProps,
    timePickerProps?: TimePickerProps,
    showLabels?: boolean,
    dateLabelText?: string | JSX.Element,
    dateLabelProps?: HTMLAttributes<HTMLSpanElement>
    timeLabelText?: string | JSX.Element,
    timeLabelProps?: HTMLAttributes<HTMLSpanElement>
    style?: React.CSSProperties,
    size?: 'small' | 'default' | 'large',
    utc?: boolean,
    disabled?: boolean,
    disabledReason?: string
}

const DateTimePicker: React.FC<DateTimePickerProps> = props => {

    function asMoment(value: string | Moment){
        if (value instanceof moment){
            return value as Moment;
        }
        return momentOrNull(value);
    }

    let dateVal = asMoment(props.value?.date);
    let timeVal = asMoment(props.value?.time);
    const dateDisabledReason = (props.datePickerProps?.disabled ?? props.disabled) ? props.disabledReason : undefined;
    const timeDisabledReason = (props.timePickerProps?.disabled ?? props.disabled) ? props.disabledReason : undefined;
    // Ant Design 3 supports trigger arrays at runtime but declares only a single trigger.
    const tooltipTriggers = ['hover', 'focus'] as unknown as TooltipTrigger;

    // function handleChange(value: string){
    //     let momVal = momentOrNull(value);
    //     if (momVal){
    //         props.onChange?.(momVal)
    //     }
    //     else
    //     {
    //         props.onChange?.(null)
    //     }
    // }

    let getLabel = (props: any, text: any, defaultText: string) => <span className={cn(BASE_CLS + "-label", props.className)} {...props}>{text || defaultText}</span>

    return <span style={props.style}>
        {props.showLabels ? getLabel(props.dateLabelProps, props.dateLabelText, 'Date:') : null}
        <Tooltip title={dateDisabledReason} trigger={tooltipTriggers}>
            <span
                style={{display: 'inline-block'}}
                tabIndex={dateDisabledReason ? 0 : undefined}
                aria-label={dateDisabledReason ? `Date unavailable: ${dateDisabledReason}` : undefined}
            >
                <DatePicker
                    size={props.size || "small"}
                    style={{ marginRight: '6px', width: 110 }}
                    value={dateVal}
                    format="MM/DD/YYYY"
                    allowClear={false}
                    onChange={(date) => {
                        props.onChange?.(new DateTimePickerValue(date, timeVal))
                    }}
                    placeholder=""
                    disabled={props.disabled}
                    {...props.datePickerProps}
                />
            </span>
        </Tooltip>
        {props.showLabels ? getLabel(props.timeLabelProps, props.timeLabelText, 'Time:') : null}
        <Tooltip title={timeDisabledReason} trigger={tooltipTriggers}>
            <span
                style={{display: 'inline-block'}}
                tabIndex={timeDisabledReason ? 0 : undefined}
                aria-label={timeDisabledReason ? `Time unavailable: ${timeDisabledReason}` : undefined}
            >
                <TimePicker
                    size={props.size || "small"}
                    value={timeVal}
                    format="HH:mm"
                    allowClear={false}
                    style={{ width: 70 }}
                    onChange={(time) => {
                        props.onChange?.(new DateTimePickerValue(dateVal, time))
                    }}
                    placeholder=""
                    disabled={props.disabled}
                    {...props.timePickerProps}
                />
            </span>
        </Tooltip>
    </span>
}

export function strToDateTimePickerValue(dt: string | any): DateTimePickerValue {
    if (dt instanceof DateTimePickerValue){
        return dt;
    }
    return DateTimePickerValue.from(dt);
}

DateTimePicker.defaultProps = {
    showLabels: false
}

export default DateTimePicker
